import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Shield, Building2, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const ROLES = [
  { value: 'ADMIN_ESTADUAL', label: 'Admin Estadual', description: 'Acesso completo ao sistema' },
  { value: 'CASA_CIVIL', label: 'Casa Civil', description: 'Gestão estratégica de atos' },
  { value: 'CGE', label: 'CGE', description: 'Controle e conformidade' },
  { value: 'SECRETARIA_EXECUTORA', label: 'Secretaria Executora', description: 'Execução de planos de ação' },
  { value: 'UNIDADE_INTERNA', label: 'Unidade Interna', description: 'Upload de evidências' },
  { value: 'JURIDICO', label: 'Jurídico', description: 'Análise jurídica' },
  { value: 'GOVERNADOR', label: 'Governador', description: 'Visualização executiva' },
  { value: 'OBSERVADOR_EXTERNO', label: 'Observador Externo', description: 'Apenas leitura' },
];

const STATES = [
  { id: 'AC', name: 'Acre' },
  { id: 'AL', name: 'Alagoas' },
  { id: 'AP', name: 'Amapá' },
  { id: 'AM', name: 'Amazonas' },
  { id: 'BA', name: 'Bahia' },
  { id: 'CE', name: 'Ceará' },
  { id: 'DF', name: 'Distrito Federal' },
  { id: 'ES', name: 'Espírito Santo' },
  { id: 'GO', name: 'Goiás' },
  { id: 'MA', name: 'Maranhão' },
  { id: 'MT', name: 'Mato Grosso' },
  { id: 'MS', name: 'Mato Grosso do Sul' },
  { id: 'MG', name: 'Minas Gerais' },
  { id: 'PA', name: 'Pará' },
  { id: 'PB', name: 'Paraíba' },
  { id: 'PR', name: 'Paraná' },
  { id: 'PE', name: 'Pernambuco' },
  { id: 'PI', name: 'Piauí' },
  { id: 'RJ', name: 'Rio de Janeiro' },
  { id: 'RN', name: 'Rio Grande do Norte' },
  { id: 'RS', name: 'Rio Grande do Sul' },
  { id: 'RO', name: 'Rondônia' },
  { id: 'RR', name: 'Roraima' },
  { id: 'SC', name: 'Santa Catarina' },
  { id: 'SP', name: 'São Paulo' },
  { id: 'SE', name: 'Sergipe' },
  { id: 'TO', name: 'Tocantins' },
];

export const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user, completeOnboarding } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedState, setSelectedState] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [step, setStep] = useState(1);

  useEffect(() => {
    if (user && user.tenant_id !== 'default') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleComplete = async () => {
    if (!selectedState || !selectedRole) {
      toast.error('Por favor, selecione o estado e o perfil');
      return;
    }

    setLoading(true);
    const result = await completeOnboarding(selectedState, selectedRole);
    setLoading(false);

    if (result.success) {
      toast.success('Configuração concluída!');
      navigate('/dashboard');
    } else {
      toast.error(result.error || 'Erro ao completar configuração');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg shadow-xl">
        <CardHeader className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-[#1d4ed8] flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold">Bem-vindo ao BlueGov</CardTitle>
          <CardDescription>
            {user?.name}, configure sua conta para começar
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Progress indicator */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className={`w-3 h-3 rounded-full ${step >= 1 ? 'bg-[#1d4ed8]' : 'bg-slate-200'}`} />
            <div className={`w-12 h-1 ${step >= 2 ? 'bg-[#1d4ed8]' : 'bg-slate-200'}`} />
            <div className={`w-3 h-3 rounded-full ${step >= 2 ? 'bg-[#1d4ed8]' : 'bg-slate-200'}`} />
          </div>

          {step === 1 && (
            <div className="space-y-4 animate-fade-in">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  Selecione seu Estado
                </label>
                <Select value={selectedState} onValueChange={(v) => { setSelectedState(v); }}>
                  <SelectTrigger className="w-full" data-testid="onboarding-state-select">
                    <Building2 className="w-4 h-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="Escolha o estado" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATES.map((state) => (
                      <SelectItem key={state.id} value={state.id}>
                        {state.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                className="w-full bg-[#1d4ed8] hover:bg-[#1e40af]"
                onClick={() => selectedState && setStep(2)}
                disabled={!selectedState}
                data-testid="onboarding-next-btn"
              >
                Continuar
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4 animate-fade-in">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  Selecione seu Perfil
                </label>
                <div className="grid gap-2">
                  {ROLES.map((role) => (
                    <button
                      key={role.value}
                      type="button"
                      className={`p-4 rounded-lg border text-left transition-all ${
                        selectedRole === role.value
                          ? 'border-[#1d4ed8] bg-[#1d4ed8]/5'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => setSelectedRole(role.value)}
                      data-testid={`role-option-${role.value}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-900">{role.label}</p>
                          <p className="text-xs text-slate-500">{role.description}</p>
                        </div>
                        {selectedRole === role.value && (
                          <CheckCircle className="w-5 h-5 text-[#1d4ed8]" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setStep(1)}
                  data-testid="onboarding-back-btn"
                >
                  Voltar
                </Button>
                <Button
                  className="flex-1 bg-[#1d4ed8] hover:bg-[#1e40af]"
                  onClick={handleComplete}
                  disabled={!selectedRole || loading}
                  data-testid="onboarding-complete-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Concluir
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OnboardingPage;
